import {
  AgeDistribution,
  AssignmentStatusReport,
  ChildrenDistribution,
  MaritalStatusDistribution,
  MonthlyInitiations,
  MovementReport,
  TopClientReport,
  TopDestinationReport,
  TopPartnerReport,
  TopServiceReport,
  YearlyInitiations
} from '@k2/common/entities-state/types';
import { addToNow, toISODate } from '@k2/common/helpers';
import { Filter } from '@k2/common/reports/filter';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiClient } from '../api-client';

export class ReportsEndpoint {
  constructor(private api: ApiClient) {
  }

  private fetchReport = <T = any>(reportName: string) => {
    return (filters: Filter[] = []): Observable<T> => {
      const PATH = toReportPath(reportName)(filters);

      return this.api.get(PATH);
    };
  };

  private toCsvReportPath = (reportName: string) => {
    return (filters: Filter[]): string => this.api.toUrl(toReportPath(reportName)(filters));
  };

  fetchPopulationLanes = this.fetchReport('klinec/population/lanes');

  fetchServicesPerYear = this.fetchReport('klinec/year-on-year/services-per-year');

  fetchAssigneesAge = this.fetchReport<AgeDistribution[]>('klinec/demographics/assignee-age');

  fetchMaritalStatuses = this.fetchReport<MaritalStatusDistribution[]>(
    'klinec/demographics/marital-status'
  );

  fetchChildrenAmount = this.fetchReport<ChildrenDistribution[]>(
    'klinec/demographics/children-amount'
  );

  fetchStatusesReport = this.fetchReport<AssignmentStatusReport[]>('klinec/dashboard/statuses');

  fetchTop10Clients = this.fetchReport<TopClientReport[]>('klinec/overview/top-10-clients');

  fetchTop10Destinations = this.fetchReport<TopDestinationReport[]>(
    'klinec/overview/top-10-destinations'
  );

  fetchTop10Services = this.fetchReport<TopServiceReport[]>('klinec/overview/top-10-services');

  fetchTop10Partners = this.fetchReport<TopPartnerReport[]>('klinec/overview/top-10-partners');

  fetchMonthlyInitiations = this.fetchReport<MonthlyInitiations[]>('klinec/initiations-monthly');

  fetchCumulativeYearlyInitiations = this.fetchReport<YearlyInitiations[]>(
    'klinec/initiations-year-on-year/cumulative'
  );

  fetchRegionsChord = this.fetchReport('klinec/population/regions-chord');

  fetchClientContacts = this.fetchReport('klinec/mi-reporting/client-contacts');
  toClientContactsXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/client-contacts');
  toClientContactsCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/client-contacts');

  fetchInitiations = this.fetchReport('initiations');
  toInitiationsXlsPath = this.toCsvReportPath('xls/initiations');
  toInitiationsCsvPath = this.toCsvReportPath('csv/initiations');

  fetchDataMonitoring = this.fetchReport('klinec/mi-reporting/data-monitoring');
  toDataMonitoringXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/data-monitoring');
  toDataMonitoringCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/data-monitoring');

  fetchSupportedServices = this.fetchReport('klinec/mi-reporting/supported-services');
  toSupportedServicesXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/supported-services');
  toSupportedServicesCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/supported-services');

  fetchAssignmentsHistory = this.fetchReport('klinec/mi-reporting/full-assignment-history');
  toAssignmentsHistoryXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/full-assignment-history');
  toAssignmentsHistoryCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/full-assignment-history');

  fetchDocumentsHistory = this.fetchReport('klinec/mi-reporting/documents-history');
  toDocumentsHistoryXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/documents-history');
  toDocumentsHistoryCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/documents-history');

  fetchClientCustomField = this.fetchReport('klinec/mi-reporting/client-custom-field');
  toClientCustomFieldXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/client-custom-field');
  toClientCustomFieldCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/client-custom-field');

  fetchStorageReport = this.fetchReport('klinec/mi-reporting/storage');
  toStorageReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/storage');
  toStorageReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/storage');

  fetchServicesInProgressReport = this.fetchReport('klinec/mi-reporting/services-in-progress');
  toServicesInProgressReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/services-in-progress'
  );
  toServicesInProgressReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/services-in-progress'
  );

  fetchNonTenancyManagementReport = this.fetchReport(
    'klinec/mi-reporting/non-tenancy-management-expiry'
  );
  toNonTenancyManagementReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/non-tenancy-management-expiry'
  );
  toNonTenancyManagementReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/non-tenancy-management-expiry'
  );

  fetchTempAccommodationExpiryReport = this.fetchReport(
    'klinec/mi-reporting/temp-accommodation-expiry'
  );
  toTempAccommodationExpiryReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/temp-accommodation-expiry'
  );
  toTempAccommodationExpiryReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/temp-accommodation-expiry'
  );

  fetchHHGVolumesReport = this.fetchReport('klinec/mi-reporting/hhg-volumes');
  toHHGVolumesReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/hhg-volumes');
  toHHGVolumesReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/hhg-volumes');

  fetchColaReport = this.fetchReport('klinec/mi-reporting/cola');
  toColaReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/cola');
  toColaReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/cola');

  fetchMortgageSubsidyReport = this.fetchReport('klinec/mi-reporting/mortgage-subsidy');
  toMortgageSubsidyReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/mortgage-subsidy');
  toMortgageSubsidyReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/mortgage-subsidy');

  fetchHHGPartnerDocumentStatusReport = this.fetchReport('klinec/mi-reporting/hhg_partner_document_status');
  toHHGPartnerDocumentStatusReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/hhg_partner_document_status');
  toHHGPartnerDocumentStatusReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/hhg_partner_document_status');

  fetchInitiationsListReport = this.fetchReport('klinec/mi-reporting/initiations-list');
  toInitiationsListReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/initiations-list');
  toInitiationsListReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/initiations-list');

  fetchServicesBasicDataReport = this.fetchReport('klinec/mi-reporting/services-basic-data-report');
  toServicesBasicDataReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/services-basic-data-report');
  toServicesBasicDataReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/services-basic-data-report');

  fetchInsuranceClaimsReport = this.fetchReport('klinec/mi-reporting/hhg-insurance-claims');
  toInsuranceClaimsReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/hhg-insurance-claims'
  );
  toInsuranceClaimsReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/hhg-insurance-claims'
  );

  fetchTenancyInvoicingReport = this.fetchReport('klinec/mi-reporting/tenancy-invoicing');
  toTenancyInvoicingReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/tenancy-invoicing'
  );
  toTenancyInvoicingReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/tenancy-invoicing'
  );

  fetchTenancyExpiryReport = this.fetchReport('klinec/mi-reporting/tenancy-expiry');
  toTenancyExpiryReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/tenancy-expiry');
  toTenancyExpiryReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/tenancy-expiry');

  fetchVisaImmigrationReport = this.fetchReport('klinec/mi-reporting/visa-immigration');
  toVisaImmigrationReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/visa-immigration');
  toVisaImmigrationReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/visa-immigration');

  fetchAssignmentsUnansweredFeedbackQuesionsReport = this.fetchReport(
    'klinec/mi-reporting/assignments-unanswered-feedback-questions'
  );
  toAssignmentsUnansweredFeedbackQuesionsReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/assignments-unanswered-feedback-questions'
  );
  toAssignmentsUnansweredFeedbackQuesionsReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/assignments-unanswered-feedback-questions'
  );

  fetchAssignmentsFeedbackReport = this.fetchReport(
    'klinec/mi-reporting/assignments-feedback-report'
  );
  toAssignmentsFeedbackReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/assignments-feedback-report'
  );
  toAssignmentsFeedbackReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/assignments-feedback-report'
  );

  fetchAssignmentsWithoutFeedbackReport = this.fetchReport(
    'klinec/mi-reporting/assignments-without-feedback'
  );

  toAssignmentsWithoutFeedbackReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/assignments-without-feedback'
  );
  toAssignmentsWithoutFeedbackReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/assignments-without-feedback'
  );

  fetchHHGDeliveryReport = this.fetchReport('klinec/mi-reporting/hhg-delivery');
  fetchHHGActivityDateReport = this.fetchReport('klinec/mi-reporting/hhg-activity');
  toHHGDeliveryReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/hhg-delivery');
  toHHGDeliveryReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/hhg-delivery');

  fetchRecurringPOPaymentsReport = this.fetchReport('klinec/mi-reporting/recurring-po-payments');
  toRecurringPOPaymentsXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/recurring-po-payments');
  toRecurringPOPaymentsCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/recurring-po-payments');

  fetchRecurringSOPaymentsReport = this.fetchReport('klinec/mi-reporting/recurring-so-payments');
  toRecurringSOPaymentsXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/recurring-so-payments');
  toRecurringSOPaymentsCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/recurring-so-payments');

  fetchProductGroups = (serviceId: number) => {
    return this.api
      .get(`/reports/klinec/service/${serviceId}/list`)
      .pipe(map(payload => payload.items));
  };
  toHHGDActivityDateReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/hhg-activity');
  toHHGDActivityDateReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/hhg-activity');

  fetchPendingSalesTransactionsReport = this.fetchReport(
    'klinec/mi-reporting/pending-sales-transactions'
  );
  toPendingSalesTransactionsReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/pending-sales-transactions'
  );
  toPendingSalesTransactionsReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/pending-sales-transactions'
  );

  fetchGlobalAssignmentsFeedbackReport = this.fetchReport(
    'klinec/mi-reporting/global-assignments-feedback-report'
  );

  toGlobalAssignmentsFeedbackReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/global-assignments-feedback-report'
  );

  toGlobalAssignmentsFeedbackReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/global-assignments-feedback-report'
  );

  fetchHhgQcFeedbackReport = this.fetchReport(
    'klinec/mi-reporting/hhg-qc-feedback-report'
  );

  toHhgQcFeedbackReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/hhg-qc-feedback-report'
  );

  toHhgQcFeedbackReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/hhg-qc-feedback-report'
  );

  fetchHhgCostsDoorToDoorReport = this.fetchReport(
    'klinec/mi-reporting/hhg-costs-door-to-door-report'
  );

  toHhgCostsDoorToDoorReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/hhg-costs-door-to-door-report'
  );

  toHhgCostsDoorToDoorReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/hhg-costs-door-to-door-report'
  );

  fetchHhgSpecificCostsReport = this.fetchReport(
    'klinec/mi-reporting/hhg-specific-costs-report'
  );

  toHhgSpecificCostsReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/hhg-specific-costs-report'
  );

  toHhgSpecificCostsReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/hhg-specific-costs-report'
  );

  fetchHHGShipmentHistoryReport = this.fetchReport('klinec/mi-reporting/hhg-shipment-history');
  toHHGShipmentHistoryReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/hhg-shipment-history'
  );
  toHHGShipmentHistoryReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/hhg-shipment-history'
  );

  fetchExceptionTrackingReport = this.fetchReport('klinec/mi-reporting/exception-tracking');
  toExceptionTrackingReportXlsPath = this.toCsvReportPath(
    'klinec/xls/mi-reporting/exception-tracking'
  );
  toExceptionTrackingReportCsvPath = this.toCsvReportPath(
    'klinec/csv/mi-reporting/exception-tracking'
  );

  fetchCostSavingsReport = this.fetchReport('klinec/mi-reporting/cost-savings');
  toCostSavingsReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/cost-savings');
  toCostSavingsReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/cost-savings');

  fetchPartnerDataReport = this.fetchReport('klinec/mi-reporting/partner-data');
  toPartnerDataReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/partner-data');
  toPartnerDataReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/partner-data');

  fetchPurchaseOrdersReport = this.fetchReport('klinec/mi-reporting/purchase-order-report');
  toPurchaseOrdersReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/purchase-order-report');
  toPurchaseOrdersReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/purchase-order-report');

  fetchSalesOrdersReport = this.fetchReport('klinec/mi-reporting/sales-order-report');
  toSalesOrdersReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/sales-order-report');
  toSalesOrdersReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/sales-order-report');

  fetchRevenueRecognitionFinanceDatesReport = this.fetchReport('klinec/mi-reporting/revenue-recognition-finance-dates');
  toRevenueRecognitionFinanceDatesReportXlsPath = this.toCsvReportPath('klinec/xls/mi-reporting/revenue-recognition-finance-dates');
  toRevenueRecognitionFinanceDatesReportCsvPath = this.toCsvReportPath('klinec/csv/mi-reporting/revenue-recognition-finance-dates');

  fetchPopulationMovementReport = (clientId: number): Observable<MovementReport> => {
    const fetch = this.fetchReport('klinec/population/movements');

    return combineLatest([movementsBy('start_date'), movementsBy('end_date')]).pipe(
      map(([aboutToStart, aboutToEnd]) => ({ aboutToStart, aboutToEnd }))
    );

    function movementsBy(type: string) {
      return fetch([
        new ClientIdFilter(clientId),
        { name: type, value: `${toISODate(new Date())}_${toISODate(addToNow(90))}` }
      ]);
    }
  };

  fetchFeedbackReport = (clientId: number) => {
    return this.api.get(`/staff/client/${clientId}/report/feedback`);
  };

  fetchClientStorageReport = (clientId: number) => {
    return this.api.get(`/reports/client/${clientId}/report/storage`);
  };

  fetchYearOnYearReports = (clientId: number) => {
    return this.api
      .get(`/reports/client/${clientId}/report/year-on-year`)
      .pipe(map(payload => payload.reports));
  };
}

export class ClientIdFilter implements Filter {
  readonly name = 'client_id';
  readonly value: number;

  constructor(id: number) {
    this.value = id;
  }
}

function toReportPath(reportName: string) {
  return (filters: Filter[]): string => {
    return [
      `/reports/${reportName}`,
      ...filters.map(({ name, value }) => {
        return value?.length ? `${name}/${value.toString()}` : `${name}/${encodeURIComponent(value)}`;
      })
    ].join('/');
  };
}
